import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { getChannel } from "@/mocks/channels";
import { formatDateShort, formatViews, toneClasses } from "@/lib/ui";

interface ArticleCardProps {
  article: Article;
  variant?: "featured" | "default" | "row" | "compact";
  rank?: number;
}

export default function ArticleCard({ article, variant = "default", rank }: ArticleCardProps) {
  const channel = getChannel(article.channel);
  const tone = toneClasses[channel?.tone ?? "primary"];
  const href = `/wenzhang/${article.slug}`;

  if (variant === "compact") {
    return (
      <Link
        to={href}
        className="group flex items-start gap-3 rounded-md px-2 py-3 transition-colors hover:bg-background-100"
      >
        {typeof rank === "number" && (
          <span
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-heading text-xs font-bold ${
              rank <= 3 ? `${tone.solid} text-background-50` : "bg-background-200 text-foreground-700"
            }`}
          >
            {rank}
          </span>
        )}
        <span className="flex min-w-0 flex-col">
          <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
            <span>{channel?.name}</span>
            <span className="text-background-400">·</span>
            <span>{formatViews(article.views)}阅读</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "row") {
    return (
      <Link
        to={href}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-background-100"
      >
        <span className="relative block h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-40">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title}`}
            className="h-full w-full object-top"
          />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-2">
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone.bgSoft} ${tone.textStrong}`}>
              {channel?.name}
            </span>
            <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
          </span>
          <span className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1.5 hidden line-clamp-2 text-sm leading-relaxed text-foreground-600 sm:block">
            {article.excerpt}
          </span>
          <span className="mt-auto flex items-center gap-3 pt-2 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>{formatDateShort(article.date)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={href}
        className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
      >
        <span className="relative block h-56 w-full overflow-hidden bg-background-200 sm:h-64">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title}`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <span className="absolute left-3 top-3 rounded bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
            编辑精选
          </span>
        </span>
        <span className="flex flex-col p-5">
          <span className={`text-xs font-medium ${tone.textSoft}`}>{channel?.name}</span>
          <span className="mt-2 font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
            {article.title}
          </span>
          <span className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.excerpt}
          </span>
          <span className="mt-4 flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span className="text-background-400">·</span>
            <span>{article.readTime} 分钟阅读</span>
            <span className="text-background-400">·</span>
            <span>{formatViews(article.views)}阅读</span>
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <span className="relative block h-44 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title}`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </span>
      <span className="flex flex-1 flex-col p-4">
        <span className={`text-xs font-medium ${tone.textSoft}`}>{channel?.name}</span>
        <span className="mt-1.5 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {article.title}
        </span>
        <span className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </span>
        <span className="mt-auto flex items-center gap-3 pt-3 text-xs text-foreground-500">
          <span>{formatDateShort(article.date)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {article.readTime} 分钟
          </span>
        </span>
      </span>
    </Link>
  );
}